import { useQuery } from '@tanstack/react-query'
import { supabaseClient } from '~/shared/common/supabase/client'
import { useAuthStore } from '~/client/common/stores/auth'
import type { EventSpaceList } from '~/shared/event/event.types'

async function fetchEvents(params?: { page?: number; page_size?: number }): Promise<{ data: EventSpaceList[]; count: number | null }> {
  const { data: { session } } = await supabaseClient.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const search = new URLSearchParams()
  if (params?.page) search.set('page', String(params.page))
  if (params?.page_size) search.set('page_size', String(params.page_size))
  const qs = search.toString()

  const res = await fetch(`/api/listEvents${qs ? `?${qs}` : ''}`, {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? `Failed to load events (${res.status})`)
  }

  const json = (await res.json()) as { data: EventSpaceList[]; count: number | null }
  return json
}

export const useEvents = (params?: { page?: number; page_size?: number }) => {
  const isAuthReady = useAuthStore((s) => s.initialized && !!s.user)

  const { data, isLoading, error } = useQuery({
    queryKey: ['events', params?.page ?? 1, params?.page_size ?? 200],
    queryFn: () => fetchEvents(params),
    enabled: isAuthReady,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })

  return { data: (data?.data as EventSpaceList[]) || [], count: data?.count ?? 0, isLoading, error } as const
}
